import React, { useMemo, useRef, useState, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Grid } from "@react-three/drei";
import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";

const ACCENT = "#7c4dff";
const ACCENT_DIM = "#b39ddb";
const SCAN = "#00e5ff";

/**
 * Parse a .ply into a Three.js BufferGeometry. Accepts either a raw ArrayBuffer
 * (binary over socket.io — the fast path) or a base64 string (back-compat).
 */
function parsePly(data: ArrayBuffer | string): THREE.BufferGeometry | null {
  try {
    let buffer: ArrayBuffer;
    if (typeof data === "string") {
      const binary = atob(data);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
      buffer = bytes.buffer;
    } else {
      buffer = data;
    }
    const loader = new PLYLoader();
    const geometry = loader.parse(buffer);
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    return geometry;
  } catch (e) {
    console.error("Failed to parse .ply:", e);
    return null;
  }
}

type NormInfo = {
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  /** normalized cloud radius (post-scale) for framing scan/grid */
  radius: number;
  /** normalized min-Y (post-scale, centered) so we can sit the grid under it */
  minY: number;
};

// The RealSense capture (with the Nexus-side Z-forward/Y-down flip) comes in
// standing "up" toward the camera. Tilt it -90° about X so the depth axis lies
// down onto the horizontal grid — i.e. the captured surface faces the viewer
// flat instead of appearing as a vertical wall.
const CLOUD_TILT_X = -Math.PI / 2;

function useNormalized(geometry: THREE.BufferGeometry, version = 0): NormInfo {
  return useMemo(() => {
    const s = (() => {
      const sphere = geometry.boundingSphere;
      const r = sphere ? sphere.radius : 1;
      return r > 0 ? 1.6 / r : 1;
    })();

    // Compute the bounding box in the SAME orientation the cloud is rendered
    // (after tilt + scale), so centering and the grid/scan floor line up with
    // what's actually on screen.
    const m = new THREE.Matrix4()
      .makeRotationX(CLOUD_TILT_X)
      .multiply(new THREE.Matrix4().makeScale(s, s, s));
    const oriented = geometry.clone().applyMatrix4(m);
    oriented.computeBoundingBox();
    const box = oriented.boundingBox!;
    const c = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    return {
      // position/rotation are applied by three in order: the mesh is rotated
      // then translated, so translate by the ORIENTED center to sit at origin.
      position: [-c.x, -c.y, -c.z],
      scale: s,
      rotation: [CLOUD_TILT_X, 0, 0],
      radius: Math.max(size.x, size.z) * 0.5 || 1.6,
      minY: -size.y * 0.5,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometry, version]);
}

/**
 * The point cloud, rendered with additive-ish glow. A horizontal "scan plane"
 * sweeps up and down the model; points near the plane flare brighter, giving a
 * live 3D-scanner readout feel. Implemented with an onBeforeCompile shader
 * injection so it stays a cheap single draw call.
 */
const ScanCloud = ({
  geometry,
  geometryVersion,
  norm,
  scanning,
  pointSize,
  progress,
}: {
  geometry: THREE.BufferGeometry;
  geometryVersion: number;
  norm: NormInfo;
  scanning: boolean;
  pointSize: number;
  progress?: number | null;
}) => {
  const matRef = useRef<THREE.PointsMaterial & { userData: any }>(null);
  const uniforms = useRef({
    uScanY: { value: 0 },
    uScanOn: { value: scanning ? 1 : 0 },
    uMinY: { value: norm.minY },
    uMaxY: { value: -norm.minY },
    uScanColor: { value: new THREE.Color(SCAN) },
  });

  useEffect(() => {
    // While streaming, keep the scan band lit even if the user toggled "scan"
    // off, so the sweep reads as the loading frontier.
    uniforms.current.uScanOn.value = scanning || (progress != null && progress < 1) ? 1 : 0;
    uniforms.current.uMinY.value = norm.minY;
    uniforms.current.uMaxY.value = -norm.minY;
  }, [scanning, norm.minY, progress]);

  // As points are appended during streaming, tell three the position/color
  // attributes grew so the new vertices get uploaded and drawn.
  useEffect(() => {
    const pos = geometry.getAttribute("position");
    if (pos) (pos as THREE.BufferAttribute).needsUpdate = true;
    const col = geometry.getAttribute("color");
    if (col) (col as THREE.BufferAttribute).needsUpdate = true;
  }, [geometry, geometryVersion]);

  const hasColor = !!geometry.getAttribute("color");

  useFrame(({ clock }) => {
    const span = -norm.minY - norm.minY || 3.2;
    if (progress != null && progress < 1) {
      // Honest progress: the scan plane sits at the height frontier that has
      // been streamed so far (points arrive sorted bottom->top).
      uniforms.current.uScanY.value = norm.minY + Math.min(Math.max(progress, 0), 1) * span;
    } else {
      const t = (Math.sin(clock.elapsedTime * 0.9) * 0.5 + 0.5); // 0..1
      uniforms.current.uScanY.value = norm.minY + t * span;
    }
  });

  return (
    <points
      geometry={geometry}
      scale={norm.scale}
      position={norm.position}
      rotation={norm.rotation}
    >
      <pointsMaterial
        ref={matRef as any}
        size={pointSize}
        sizeAttenuation
        vertexColors={hasColor}
        color={hasColor ? undefined : ACCENT_DIM}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending as any}
        onBeforeCompile={(shader) => {
          shader.uniforms.uScanY = uniforms.current.uScanY;
          shader.uniforms.uScanOn = uniforms.current.uScanOn;
          shader.uniforms.uScanColor = uniforms.current.uScanColor;
          shader.vertexShader =
            "varying float vWorldY;\n" +
            shader.vertexShader.replace(
              "#include <begin_vertex>",
              "#include <begin_vertex>\n  vWorldY = (modelMatrix * vec4(transformed, 1.0)).y;"
            );
          shader.fragmentShader =
            "uniform float uScanY;\nuniform float uScanOn;\nuniform vec3 uScanColor;\nvarying float vWorldY;\n" +
            shader.fragmentShader
              .replace(
                "#include <clipping_planes_fragment>",
                "#include <clipping_planes_fragment>\n" +
                "  float d = abs(vWorldY - uScanY);\n" +
                "  float band = smoothstep(0.09, 0.0, d) * uScanOn;\n"
              )
              .replace(
                "#include <premultiplied_alpha_fragment>",
                "  gl_FragColor.rgb = mix(gl_FragColor.rgb, uScanColor, band);\n" +
                "  gl_FragColor.rgb += uScanColor * band * 0.6;\n" +
                "  #include <premultiplied_alpha_fragment>"
              );
        }}
      />
    </points>
  );
};

/** Render the object as a solid, lit, vertex-colored surface mesh. */
const MeshSurface = ({ geometry, norm }: { geometry: THREE.BufferGeometry; norm: NormInfo }) => {
  // Ensure normals exist for lighting (grid meshes from PLYLoader may lack them).
  const geom = useMemo(() => {
    if (!geometry.getAttribute("normal")) {
      try { geometry.computeVertexNormals(); } catch (_e) { /* noop */ }
    }
    return geometry;
  }, [geometry]);
  const hasColor = !!geom.getAttribute("color");
  return (
    <mesh geometry={geom} scale={norm.scale} position={norm.position} rotation={norm.rotation}>
      <meshStandardMaterial
        vertexColors={hasColor}
        color={hasColor ? "#ffffff" : "#b39ddb"}
        roughness={0.75}
        metalness={0.05}
        side={THREE.DoubleSide}
        flatShading={false}
      />
    </mesh>
  );
};

/** Soft rotating boundary ring around the model — holo-scanner base. */
const HoloRing = ({ y, radius }: { y: number; radius: number }) => {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((_, dt) => {
    if (ref.current) ref.current.rotation.z += dt * 0.25;
  });
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]}>
      <ringGeometry args={[radius * 1.15, radius * 1.18, 96]} />
      <meshBasicMaterial color={SCAN} transparent opacity={0.5} side={THREE.DoubleSide} />
    </mesh>
  );
};

/** Imperatively drive the OrbitControls ref for the "reset view" action. */
const ControlsRig = React.forwardRef<any, { autoRotate: boolean }>(({ autoRotate }, ref) => {
  const { camera } = useThree();
  useEffect(() => {
    camera.position.set(0, 1.6, 2.2);
  }, [camera]);
  return (
    <OrbitControls
      ref={ref as any}
      makeDefault
      autoRotate={autoRotate}
      autoRotateSpeed={0.9}
      enableDamping
      dampingFactor={0.1}
      rotateSpeed={0.6}
      zoomSpeed={0.8}
      panSpeed={0.6}
      minDistance={0.6}
      maxDistance={14}
      target={[0, 0, 0]}
    />
  );
});
ControlsRig.displayName = "ControlsRig";

type Props = {
  /** .ply data: raw ArrayBuffer (fast binary path) or base64 string (back-compat) */
  data: ArrayBuffer | string | null;
  height?: number | string;
  /** initial auto-orbit state (user can toggle it) */
  autoRotate?: boolean;
  /** label shown in the HUD, e.g. "SCENE" | "OBJECT" */
  label?: string;
  /**
   * Pre-built geometry (used for progressive streaming) — takes precedence over
   * `data`. Provide a monotonically-increasing `geometryVersion` so the viewer
   * knows to refresh as points are appended.
   */
  geometryOverride?: THREE.BufferGeometry | null;
  geometryVersion?: number;
  /** streaming progress 0..1 (drives the progress bar + scan sweep). null = not streaming */
  progress?: number | null;
  /** optional detail readout under the progress bar, e.g. "12,400 / 30,189 pts" */
  progressLabel?: string;
  /** render the geometry as a solid lit surface mesh instead of a point cloud */
  renderAsMesh?: boolean;
};

const CornerBrackets = () => {
  const brk: React.CSSProperties = {
    position: "absolute",
    width: 22,
    height: 22,
    borderColor: ACCENT,
    pointerEvents: "none",
  };
  return (
    <>
      <div style={{ ...brk, top: 8, left: 8, borderTop: `2px solid ${ACCENT}`, borderLeft: `2px solid ${ACCENT}` }} />
      <div style={{ ...brk, top: 8, right: 8, borderTop: `2px solid ${ACCENT}`, borderRight: `2px solid ${ACCENT}` }} />
      <div style={{ ...brk, bottom: 8, left: 8, borderBottom: `2px solid ${ACCENT}`, borderLeft: `2px solid ${ACCENT}` }} />
      <div style={{ ...brk, bottom: 8, right: 8, borderBottom: `2px solid ${ACCENT}`, borderRight: `2px solid ${ACCENT}` }} />
    </>
  );
};

const PillButton = ({
  active,
  onClick,
  children,
  title,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 11px",
      borderRadius: 20,
      border: `1px solid ${active ? SCAN : "rgba(124,77,255,0.5)"}`,
      background: active ? "rgba(0,229,255,0.15)" : "rgba(124,77,255,0.10)",
      color: active ? SCAN : ACCENT_DIM,
      font: "600 11px/1 ui-monospace, monospace",
      letterSpacing: 0.5,
      textTransform: "uppercase",
      cursor: "pointer",
      backdropFilter: "blur(4px)",
      transition: "all 0.15s",
    }}
  >
    {children}
  </button>
);

const PointCloudViewer = ({
  data,
  height = 440,
  autoRotate = false,
  label,
  geometryOverride = null,
  geometryVersion = 0,
  progress = null,
  progressLabel,
  renderAsMesh = false,
}: Props) => {
  const parsed = useMemo(() => (data ? parsePly(data) : null), [data]);
  // geometryOverride (streaming) wins over the static parsed .ply.
  const geometry = geometryOverride ?? parsed;
  const streaming = progress != null && progress < 1;
  const modelKey = useMemo(() => {
    if (geometryOverride) return "stream";
    return data ? String(typeof data === "string" ? data.length : data.byteLength) : "empty";
  }, [data, geometryOverride]);
  const controlsRef = useRef<any>(null);

  const [orbit, setOrbit] = useState(autoRotate);
  const [scanning, setScanning] = useState(true);
  const [size, setSize] = useState(0.014);

  // Reset toggles when the model changes (scene <-> object).
  useEffect(() => {
    setOrbit(autoRotate);
  }, [modelKey, autoRotate]);

  const resetView = () => {
    const c = controlsRef.current;
    if (c) {
      c.object.position.set(0, 1.6, 2.2);
      c.target.set(0, 0, 0);
      c.update();
    }
  };

  if (!geometry) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6a6a7a",
          background: "radial-gradient(ellipse at center, #101021 0%, #05050b 100%)",
          borderRadius: 10,
          font: "600 13px/1 ui-monospace, monospace",
          letterSpacing: 1,
        }}
      >
        NO 3D MODEL
      </div>
    );
  }

  const pointCount = geometry.getAttribute("position")?.count ?? 0;

  return (
    <div
      style={{
        position: "relative",
        height,
        width: "100%",
        background: "radial-gradient(ellipse at 50% 40%, #12122a 0%, #06060f 70%, #030308 100%)",
        borderRadius: 10,
        overflow: "hidden",
        border: "1px solid rgba(124,77,255,0.35)",
        boxShadow: "inset 0 0 60px rgba(124,77,255,0.12), 0 0 24px rgba(124,77,255,0.15)",
      }}
    >
      <Canvas
        key={modelKey}
        camera={{ position: [0, 1.6, 2.2], fov: 50 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
      >
        <SceneContents
          geometry={geometry}
          geometryVersion={geometryVersion}
          scanning={scanning}
          size={size}
          orbit={orbit}
          controlsRef={controlsRef}
          progress={progress}
          renderAsMesh={renderAsMesh}
        />
      </Canvas>

      {/* HUD overlay */}
      <CornerBrackets />

      {/* Top-left: label + live status */}
      <div style={{ position: "absolute", top: 14, left: 20, pointerEvents: "none" }}>
        <div style={{ color: ACCENT, font: "700 13px/1 ui-monospace, monospace", letterSpacing: 2 }}>
          {label ? `◈ ${label} SCAN` : "◈ 3D SCAN"}
        </div>
        <div style={{ marginTop: 5, color: SCAN, font: "600 10px/1 ui-monospace, monospace", letterSpacing: 1, opacity: (scanning || streaming) ? 1 : 0.4 }}>
          <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: SCAN, marginRight: 6, boxShadow: `0 0 6px ${SCAN}`, animation: "scanpulse 1.2s ease-in-out infinite" }} />
          {streaming ? "STREAMING DETAIL" : scanning ? "SCANNING" : "IDLE"}
        </div>
      </div>

      {/* Streaming progress bar (real network progress) */}
      {progress != null && (
        <div style={{ position: "absolute", top: 54, left: 20, right: 20, pointerEvents: "none" }}>
          <div style={{ height: 6, borderRadius: 4, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(0,229,255,0.25)", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${Math.round(Math.min(Math.max(progress, 0), 1) * 100)}%`,
                background: `linear-gradient(90deg, ${ACCENT}, ${SCAN})`,
                boxShadow: `0 0 10px ${SCAN}`,
                transition: "width 0.15s linear",
              }}
            />
          </div>
          <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", color: SCAN, font: "600 10px/1 ui-monospace, monospace", letterSpacing: 0.5 }}>
            <span>{progress >= 1 ? "COMPLETE" : "RESOLVING DETAIL…"}</span>
            <span>{progressLabel ?? `${Math.round(Math.min(progress, 1) * 100)}%`}</span>
          </div>
        </div>
      )}

      {/* Top-right: point count readout */}
      <div style={{ position: "absolute", top: 14, right: 20, textAlign: "right", pointerEvents: "none" }}>
        <div style={{ color: ACCENT_DIM, font: "700 15px/1 ui-monospace, monospace", letterSpacing: 1 }}>
          {pointCount.toLocaleString()}
        </div>
        <div style={{ marginTop: 4, color: "#66667a", font: "600 9px/1 ui-monospace, monospace", letterSpacing: 2 }}>
          POINTS
        </div>
      </div>

      {/* Bottom control bar */}
      <div
        style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 8,
          alignItems: "center",
          background: "rgba(6,6,15,0.55)",
          border: "1px solid rgba(124,77,255,0.3)",
          borderRadius: 24,
          padding: "6px 10px",
          backdropFilter: "blur(6px)",
        }}
      >
        <PillButton active={orbit} onClick={() => setOrbit((v) => !v)} title="Toggle auto-orbit">
          ⟳ Orbit
        </PillButton>
        <PillButton active={scanning} onClick={() => setScanning((v) => !v)} title="Toggle scan sweep">
          ⌁ Scan
        </PillButton>
        <PillButton onClick={resetView} title="Reset camera">
          ⊹ Reset
        </PillButton>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 4px" }}>
          <span style={{ color: "#66667a", font: "600 9px/1 ui-monospace, monospace", letterSpacing: 1 }}>PT</span>
          <input
            type="range"
            min={0.006}
            max={0.03}
            step={0.002}
            value={size}
            onChange={(e) => setSize(parseFloat(e.target.value))}
            style={{ width: 70, accentColor: ACCENT }}
            title="Point size"
          />
        </div>
      </div>

      {/* Hint */}
      <div style={{ position: "absolute", bottom: 14, left: 20, color: "#55556a", font: "600 9px/1 ui-monospace, monospace", letterSpacing: 0.5, pointerEvents: "none" }}>
        DRAG ROTATE · SCROLL ZOOM · RIGHT-DRAG PAN
      </div>

      <style>{`@keyframes scanpulse { 0%,100% { opacity: 1 } 50% { opacity: 0.25 } }`}</style>
    </div>
  );
};

/** Everything inside the Canvas, so hooks (useThree/useFrame) are valid. */
const SceneContents = ({
  geometry,
  geometryVersion,
  scanning,
  size,
  orbit,
  controlsRef,
  progress,
  renderAsMesh,
}: {
  geometry: THREE.BufferGeometry;
  geometryVersion: number;
  scanning: boolean;
  size: number;
  orbit: boolean;
  controlsRef: React.MutableRefObject<any>;
  progress?: number | null;
  renderAsMesh?: boolean;
}) => {
  const norm = useNormalized(geometry, geometryVersion);
  return (
    <>
      <ambientLight intensity={renderAsMesh ? 0.55 : 0.9} />
      {/* Extra lights so the solid mesh reads as a lit 3D surface. */}
      {renderAsMesh && (
        <>
          <directionalLight position={[2, 4, 3]} intensity={1.1} />
          <directionalLight position={[-3, 2, -2]} intensity={0.5} color="#9fc7ff" />
        </>
      )}
      <fog attach="fog" args={["#06060f", 6, 16]} />

      {/* Holo grid floor beneath the model */}
      <Grid
        position={[0, norm.minY - 0.02, 0]}
        args={[10, 10]}
        cellSize={0.25}
        cellThickness={0.6}
        cellColor={"#2a2a55"}
        sectionSize={1}
        sectionThickness={1}
        sectionColor={ACCENT}
        fadeDistance={9}
        fadeStrength={1.2}
        infiniteGrid
      />

      <HoloRing y={norm.minY - 0.01} radius={norm.radius} />

      {renderAsMesh ? (
        <MeshSurface geometry={geometry} norm={norm} />
      ) : (
        <ScanCloud geometry={geometry} geometryVersion={geometryVersion} norm={norm} scanning={scanning} pointSize={size} progress={progress} />
      )}

      <ControlsRig ref={controlsRef} autoRotate={orbit} />
    </>
  );
};

export default PointCloudViewer;
